(() => {
  const GLOBAL_KEY = '__lipoEditorialQuickCartBridge';
  const previousInstance = window[GLOBAL_KEY];

  /* Keep exactly one delegated listener in the theme editor. */
  if (previousInstance?.destroy) {
    previousInstance.destroy();
  }

  let observer = null;
  let timeoutId = null;
  let sequence = 0;
  const prefetchedUrls = new Set();

  const getEditorialTrigger = target => {
    const directTrigger = target.closest?.(
      '.editorial-product-grid__quick-cart[data-product-url]'
    );

    if (directTrigger) return directTrigger;

    return target
      .closest?.('[data-editorial-card]')
      ?.querySelector(
        '.editorial-product-grid__quick-cart[data-product-url]'
      );
  };

  const getQuickCartUrl = trigger => {
    const productPath = trigger?.dataset.productUrl;

    if (!productPath) return null;

    const url = new URL(productPath, window.location.origin);
    url.pathname = `${url.pathname.replace(/\/$/, '')}/`;
    url.search = '';
    url.searchParams.set('view', 'quick-cart');
    url.hash = '';

    return url.href;
  };

  /* Warm only after user intent; do not download four drawers on page load. */
  const prefetchQuickCart = trigger => {
    const url = getQuickCartUrl(trigger);

    if (!url || prefetchedUrls.has(url)) return;

    prefetchedUrls.add(url);

    window
      .fetch(url, {
        credentials: 'same-origin',
        cache: 'default'
      })
      .then(response => {
        if (!response.ok) {
          prefetchedUrls.delete(url);
          return null;
        }

        /* Finish reading the response so it is available to the HTTP cache. */
        return response.text();
      })
      .catch(() => {
        prefetchedUrls.delete(url);
      });
  };

  const disconnectObserver = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const cancelPendingRequest = () => {
    sequence += 1;
    disconnectObserver();
  };

  const cloneEditorialMedia = trigger => {
    const mediaContainer = trigger
      .closest('[data-editorial-card]')
      ?.querySelector('[data-editorial-media]');

    const mediaSource = mediaContainer?.matches('picture, img, video')
      ? mediaContainer
      : mediaContainer?.querySelector('picture, img, video');

    if (mediaSource) {
      return mediaSource.cloneNode(true);
    }

    if (trigger.dataset.editorialImageUrl) {
      const image = document.createElement('img');
      image.src = trigger.dataset.editorialImageUrl;
      image.alt = trigger.dataset.editorialImageAlt || '';
      image.width = 1800;
      image.height = 2250;
      return image;
    }

    return null;
  };

  const prepareMedia = media => {
    media.removeAttribute?.('id');
    media.removeAttribute?.('data-editorial-media');

    media
      .querySelectorAll?.('[id]')
      .forEach(element => element.removeAttribute('id'));

    const images = media.matches?.('img')
      ? [media]
      : Array.from(media.querySelectorAll?.('img') || []);

    images.forEach(image => {
      image.loading = 'eager';
      image.decoding = 'async';
      image.sizes = '(max-width: 749px) 100vw, 60rem';
    });

    const videos = media.matches?.('video')
      ? [media]
      : Array.from(media.querySelectorAll?.('video') || []);

    videos.forEach(video => {
      video.muted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.removeAttribute('controls');
    });

    return videos;
  };

  const mountEditorialMedia = (
    drawer,
    trigger,
    capturedMedia,
    previousProduct
  ) => {
    const drawerMain = drawer.querySelector('.quick-cart-drawer__main');
    const loadedProduct = drawerMain?.querySelector('.quick-cart-product');

    if (
      !drawerMain ||
      !loadedProduct ||
      loadedProduct === previousProduct ||
      !drawer.classList.contains('quick-cart-drawer--editorial')
    ) {
      return false;
    }

    let media = capturedMedia;

    if (!media) {
      const fallbackMedia = loadedProduct.querySelector(
        '.quick-cart-drawer__slider-wrapper ' +
          '.swiper-slide:first-child picture, ' +
          '.quick-cart-drawer__slider-wrapper ' +
          '.swiper-slide:first-child img, ' +
          '.quick-cart-drawer__slider-wrapper ' +
          '.swiper-slide:first-child video'
      );

      media = fallbackMedia?.cloneNode(true) || null;
    }

    if (!media) return false;

    disconnectObserver();

    drawerMain
      .querySelector('.quick-cart-drawer__editorial-media')
      ?.remove();

    const videos = prepareMedia(media);
    const wrapper = document.createElement('div');
    wrapper.className = 'quick-cart-drawer__editorial-media';

    if (trigger.dataset.editorialAspectRatio) {
      wrapper.style.setProperty(
        '--editorial-media-aspect-ratio',
        trigger.dataset.editorialAspectRatio
      );
    }

    wrapper.appendChild(media);
    drawerMain.appendChild(wrapper);

    videos.forEach(video => {
      const playback = video.play();
      playback?.catch?.(() => {});
    });

    return true;
  };

  const observeProductLoad = (trigger, capturedMedia) => {
    cancelPendingRequest();

    const currentSequence = sequence;
    const drawer = document.querySelector('quick-cart-drawer');

    if (!drawer) return;

    const previousProduct = drawer.querySelector(
      '.quick-cart-drawer__main .quick-cart-product'
    );

    observer = new MutationObserver(() => {
      if (currentSequence !== sequence) return;

      mountEditorialMedia(
        drawer,
        trigger,
        capturedMedia,
        previousProduct
      );
    });

    observer.observe(drawer, {
      childList: true,
      subtree: true
    });

    timeoutId = window.setTimeout(() => {
      if (currentSequence === sequence) {
        disconnectObserver();
      }
    }, 15000);
  };

  const handleDocumentClick = event => {
    const closeControl = event.target.closest?.(
      'quick-cart-drawer .button--close, ' +
        'quick-cart-drawer .quick-cart-drawer__backdrop'
    );

    if (closeControl) {
      cancelPendingRequest();
      return;
    }

    const editorialMedia = event.target.closest?.(
      '[data-editorial-card] [data-editorial-media]'
    );

    if (editorialMedia) {
      const mediaTrigger = getEditorialTrigger(editorialMedia);

      if (
        mediaTrigger &&
        !mediaTrigger.disabled &&
        !mediaTrigger.classList.contains('is--loading')
      ) {
        event.preventDefault();
        mediaTrigger.click();
      }

      return;
    }

    const trigger = event.target.closest?.('[data-product-url]');

    if (!trigger) return;

    if (trigger.dataset.quickCartMode !== 'editorial') {
      cancelPendingRequest();
      return;
    }

    const capturedMedia = cloneEditorialMedia(trigger);
    observeProductLoad(trigger, capturedMedia);
  };

  const handleIntent = event => {
    const mediaOrButton = event.target.closest?.(
      '[data-editorial-card] [data-editorial-media], ' +
        '.editorial-product-grid__quick-cart[data-product-url]'
    );

    if (!mediaOrButton) return;

    prefetchQuickCart(getEditorialTrigger(mediaOrButton));
  };

  document.addEventListener('click', handleDocumentClick, true);
  document.addEventListener('pointerover', handleIntent, true);
  document.addEventListener('pointerdown', handleIntent, true);
  document.addEventListener('focusin', handleIntent, true);

  window[GLOBAL_KEY] = {
    destroy() {
      cancelPendingRequest();
      document.removeEventListener('click', handleDocumentClick, true);
      document.removeEventListener('pointerover', handleIntent, true);
      document.removeEventListener('pointerdown', handleIntent, true);
      document.removeEventListener('focusin', handleIntent, true);
    }
  };
})();
